import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Images, Loader2, Rocket, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useSeo } from "@/hooks/useSeo";
import { useHref, useLang, useT } from "@/i18n";

const INCLUDES = {
  ms: [
    "1 senaraian perniagaan",
    "1 gambar percuma",
    "Maklumat perniagaan",
    "Nombor telefon & WhatsApp",
    "Lokasi perniagaan",
    "Pautan media sosial / laman web",
    "Halaman khas perniagaan anda",
  ],
  en: [
    "1 business listing",
    "1 free photo",
    "Business information",
    "Phone & WhatsApp number",
    "Business location",
    "Social media / website links",
    "Your own business page",
  ],
};

export default function Pricing() {
  const t = useT();
  const lang = useLang();
  const href = useHref();
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    api.get("/settings/public").then(({ data }) => setSettings(data)).catch(() => setSettings(false));
  }, []);

  useSeo({
    title: t("pricing.seo.title"),
    description: t("pricing.seo.desc"),
    canonical: `${window.location.origin}${window.location.pathname}`,
  });

  if (!settings) {
    return (
      <div className="grid h-80 place-items-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  const plans = settings.featured_plans || [];
  const galleryPhotos = (settings.max_photos || 7) - (settings.gallery_free_photos || 1);
  // Plan labels come from the backend in Malay ("1 bulan").
  const planLabel = (p) =>
    lang === "en" ? p.label.replace(/bulan/i, p.key === "1m" ? "month" : "months") : p.label;

  return (
    <div className="mx-auto max-w-6xl px-4 py-14 sm:px-8">
      <p className="text-xs uppercase tracking-[0.2em] text-slate-500">{t("pricing.eyebrow")}</p>
      <h1 className="mt-2 font-display text-4xl font-extrabold tracking-tight sm:text-5xl">
        {t("pricing.title")}
      </h1>
      <p className="mt-4 max-w-2xl text-base leading-relaxed text-slate-600">{t("pricing.subtitle")}</p>

      <section className="mt-12 grid gap-6 lg:grid-cols-2" data-testid="pricing-core">
        <div className="rounded-lg border-2 border-primary/30 bg-card p-7 shadow-sm">
          <p className="text-xs uppercase tracking-[0.18em] text-primary">{t("pricing.basic.eyebrow")}</p>
          <p data-testid="pricing-general-price" className="mt-3 font-display text-5xl font-extrabold tracking-tight">
            {settings.price_label}
          </p>
          <p className="mt-3 text-sm text-slate-600">{t("pricing.basic.desc")}</p>
          <ul className="mt-6 space-y-2.5">
            {INCLUDES[lang].map((item) => (
              <li key={item} className="flex items-start gap-2 text-sm text-slate-700">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" /> {item}
              </li>
            ))}
          </ul>
          <p className="mt-6 text-xs text-slate-500">{t("pricing.basic.note")}</p>
          <Link to={href("/submit")} className="mt-6 inline-block">
            <Button data-testid="pricing-submit-button" className="h-12 bg-primary px-7 hover:bg-[#0052CC]">
              {t("pricing.basic.cta")}
            </Button>
          </Link>
        </div>

        <div className="rounded-lg border border-border bg-card p-7 shadow-sm">
          <p className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-slate-500">
            <Images className="h-3.5 w-3.5" /> {t("pricing.gallery.eyebrow")}
          </p>
          <p data-testid="pricing-gallery-price" className="mt-3 font-display text-5xl font-extrabold tracking-tight">
            {settings.gallery_price_label}
          </p>
          <p className="mt-3 text-sm leading-relaxed text-slate-600">{t("pricing.gallery.desc")}</p>
          <p className="mt-4 rounded-md bg-accent/60 p-4 text-sm font-medium text-slate-800">
            {t("pricing.gallery.includes", { photos: galleryPhotos })}
          </p>
          <Link to={`${href("/submit")}?pakej=gallery`} className="mt-6 inline-block">
            <Button variant="outline" data-testid="pricing-upgrade-gallery-button" className="h-12 px-7">
              {t("pricing.gallery.cta")}
            </Button>
          </Link>
          <p className="mt-3 text-xs text-slate-500">{t("pricing.gallery.note")}</p>
        </div>
      </section>

      <section className="mt-14" data-testid="pricing-featured">
        <p className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-slate-500">
          <Rocket className="h-3.5 w-3.5" /> {t("pricing.featured.eyebrow")}
        </p>
        <h2 className="mt-2 text-base font-medium text-slate-600 md:text-lg">{t("pricing.featured.subtitle")}</h2>
        <div className="mt-6 grid gap-4 sm:grid-cols-3">
          {plans.map((p) => (
            <div
              key={p.key}
              data-testid={`pricing-featured-${p.key}`}
              className="rounded-lg border border-border bg-card p-6 transition-transform duration-200 hover:-translate-y-1"
            >
              <p className="text-sm font-semibold text-slate-500">{planLabel(p)}</p>
              <p className="mt-2 font-display text-4xl font-extrabold tracking-tight">{p.price_label}</p>
              <Link to={`${href("/submit")}?pakej=featured_${p.key}`} className="mt-4 block">
                <Button variant="outline" data-testid={`pricing-upgrade-featured-${p.key}`} className="h-11 w-full">
                  {t("pricing.featured.cta")}
                </Button>
              </Link>
            </div>
          ))}
        </div>
        <p className="mt-4 text-sm text-slate-500">{t("pricing.featured.note")}</p>
      </section>

      <section className="mt-14" data-testid="pricing-bundle">
        <p className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-amber-600">
          <Sparkles className="h-3.5 w-3.5" /> {t("pricing.bundle.eyebrow")}
        </p>
        <h2 className="mt-2 text-base font-medium text-slate-600 md:text-lg">{t("pricing.bundle.subtitle")}</h2>
        <div className="mt-6 grid gap-4 sm:grid-cols-3">
          {plans.map((p) => (
            <div
              key={p.key}
              data-testid={`pricing-bundle-${p.key}`}
              className="rounded-lg border border-amber-300 bg-slate-950 p-6 text-white"
            >
              <p className="text-sm font-semibold text-amber-300">{planLabel(p)}</p>
              <p className="mt-2 font-display text-4xl font-extrabold tracking-tight">{p.bundle_price_label}</p>
              <p className="mt-1 text-xs text-slate-400">
                <span className="line-through">{p.bundle_separate_label}</span> · {t("pricing.bundle.save")}{" "}
                {p.bundle_saving_label}
              </p>
              <ul className="mt-4 space-y-2 text-sm text-slate-200">
                <li className="flex items-start gap-2">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-amber-300" />{" "}
                  {t("pricing.bundle.featuredFor", { label: planLabel(p) })}
                </li>
                <li className="flex items-start gap-2">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-amber-300" />{" "}
                  {t("pricing.bundle.galleryUpTo", { photos: galleryPhotos })}
                </li>
              </ul>
              <Link to={`${href("/submit")}?pakej=bundle_${p.key}`} className="mt-5 block">
                <Button
                  data-testid={`pricing-upgrade-bundle-${p.key}`}
                  className="h-11 w-full bg-amber-400 font-semibold text-slate-950 hover:bg-amber-300"
                >
                  {t("pricing.bundle.cta")}
                </Button>
              </Link>
            </div>
          ))}
        </div>
        <p className="mt-4 text-sm text-slate-500">{t("pricing.bundle.note")}</p>
      </section>

      <section className="mt-14 rounded-lg border border-border bg-slate-50 p-6" data-testid="pricing-notes">
        <p className="text-xs uppercase tracking-[0.18em] text-slate-500">{t("pricing.notes.eyebrow")}</p>
        <ul className="mt-3 space-y-2 text-sm leading-relaxed text-slate-600">
          <li data-testid="pricing-note-general">{t("pricing.notes.basic", { price: settings.price_label })}</li>
          <li>{t("pricing.notes.change")}</li>
          <li>
            <Link to={href("/terma")} className="text-primary hover:underline">
              {t("footer.terms")}
            </Link>
          </li>
        </ul>
      </section>
    </div>
  );
}
